import {Mapper} from '@s-ui/domain'

const FIRST_INDEX = 0
const SINGLE_OFFER_TYPE = 1
const TWO_OFFER_TYPES = 2
const EMPTY_STRING = ''

export default class HttpSearchUrlResponseToSearchDataAdsValueObjectMapper extends Mapper {
  constructor({config, searchDataAdsValueObjectFactory, searchRequestValueObjectFactory}) {
    super()

    this._config = config
    this._isMotos = this._config.get('APP_NAME') === this._config.get('APP_NAME_MOTOS')
    this._searchDataAdsValueObjectFactory = searchDataAdsValueObjectFactory
    this._searchRequestValueObjectFactory = searchRequestValueObjectFactory
    this._orderMap = {
      ASC: this._config.get('ORDER').ASC,
      DESC: this._config.get('ORDER').DESC
    }
    this._sort = this._config.get('search').SORT

    this._offerTypeKm0 = this._config.get('search').OFFER_TYPE_KM0
    this._offerTypeNewVehicle = this._config.get('search').OFFER_TYPE_NEW
    this._offerTypeSecondHand = this._config.get('search').OFFER_TYPE_SECOND_HAND
    this._unselectedOfferType = this._config.get('search').UNSELECTED_OFFER_TYPE
    this._noCodeRegex = this._config.get('REGEX_CODE')
    this._listingSectionIDs = this._config.get('COMMON').LISTING_SECTION_IDS
  }

  getVehicles = vehicles => {
    const hasVehicles = Boolean(vehicles?.length)
    if (!hasVehicles) return {vehicles}

    const [firstVehicle] = vehicles

    return {
      make: firstVehicle.make,
      makeId: firstVehicle.makeId,
      model: {
        id: firstVehicle.modelId,
        name: firstVehicle.model === null ? EMPTY_STRING : this._cleanInputValue({value: firstVehicle.model})
      },
      version: firstVehicle.version ? this._cleanInputValue({value: firstVehicle.version}) : undefined,
      vehicles: vehicles.map(({make, makeId, model, modelId, version}) => {
        const hasModel = Boolean(model)
        const hasVersion = Boolean(version)
        return {
          make,
          makeId,
          model: {
            id: modelId,
            name: hasModel ? this._cleanInputValue({value: model}) : undefined
          },
          version: hasVersion ? this._cleanInputValue({value: version}) : undefined
        }
      })
    }
  }

  /**
   * [1] -> /nuevo/km-0
   * [0] -> /segunda-mano/
   * [2] -> /km-0/
   * [3] -> /km-0/
   * [4] -> /km-0/
   * [5] -> /km-0/
   * [1,2,3,4,5] -> /km-0/
   * [2, X] -> /km-0/?OfferType=X
   */
  _getOfferTypeId = ({listingSectionId, offerTypeIds}) => {
    const offerTypeFilters = {
      offerTypeId: offerTypeIds[0],
      filteredOfferTypeId: undefined
    }

    if (listingSectionId === this._listingSectionIDs.ALL_VEHICLES) {
      offerTypeFilters.offerTypeId = this._unselectedOfferType
      offerTypeFilters.filteredOfferTypeId = offerTypeIds[0] ?? this._unselectedOfferType
    }

    if (this._isMotos) return offerTypeFilters

    /**
     * If there are multiple offerTypeIds
     * [1,2,3,4,5] -> /km-0/
     * [2, X] -> /km-0/?OfferType=X
     */
    if (offerTypeIds.length > SINGLE_OFFER_TYPE && offerTypeIds.indexOf(this._offerTypeKm0) >= FIRST_INDEX) {
      const filteredOfferTypeId =
        offerTypeIds.length === TWO_OFFER_TYPES
          ? offerTypeIds.find(offerType => offerType !== this._offerTypeKm0) // [2, X]
          : this._unselectedOfferType // [1,2,3,4,5]

      offerTypeFilters.offerTypeId = this._offerTypeKm0
      offerTypeFilters.filteredOfferTypeId = filteredOfferTypeId
    }

    /**
     * If there is only one offerTypeId
     * [0] -> /segunda-mano/
     * [1] -> /nuevo/km-0
     * [2] -> /km-0/
     * [3] -> /km-0/
     * [4] -> /km-0/
     * [5] -> /km-0/
     */
    if (offerTypeIds.length === SINGLE_OFFER_TYPE && offerTypeIds[0] !== this._offerTypeSecondHand) {
      offerTypeFilters.offerTypeId = this._offerTypeKm0
      offerTypeFilters.filteredOfferTypeId = offerTypeIds[0]
    }

    return offerTypeFilters
  }

  _cleanInputValue = ({value}) => {
    const decodedString = decodeURIComponent(value)
    const hasInvalidChars = this._noCodeRegex.test(decodedString)
    return hasInvalidChars ? EMPTY_STRING : decodedString
  }

  _getHasInstalment = instalment => instalment !== undefined && (instalment?.from !== null || instalment?.to !== null)

  map = ({breadcrumbs, canonical, listingSectionId, metaData, search: {filters = {}, sort, pagination}}) => {
    const {
      batteryCapacity,
      bodyTypeIds,
      categories = {},
      chargingTimeFastMode,
      chargingTimeStandardMode,
      city,
      colorIds,
      commitmentMonths,
      contractId,
      cubicCapacity,
      doors,
      drivenWheelsIds,
      electricAutonomy,
      environmentalLabels,
      equipments,
      excludedVehicles,
      fee,
      financedPrice,
      fuelTypeIds,
      hasPhoto,
      hasReservation,
      hasStock,
      hasWarranty,
      hp,
      isCertified,
      isFinanced,
      instalment,
      km,
      location,
      luggageCapacity,
      entry,
      maxTerms,
      offerTypeIds = [],
      onlyPeninsula,
      price,
      priceDrop,
      provinceIds,
      rating,
      searchText,
      seats,
      sellerTypeId,
      subscriptionVehicleState,
      transmissionTypeId,
      vehicles = [],
      year
    } = filters
    const {term, order} = sort
    const {page} = pagination
    const orderKey = order.toUpperCase()

    const hasCubicCapacity = Boolean(cubicCapacity)
    const hasDoors = Boolean(doors)
    const hasHp = Boolean(hp)
    const hasSeats = Boolean(seats)
    const hasLocation = Boolean(location)

    const {offerTypeId, filteredOfferTypeId} = this._getOfferTypeId({
      listingSectionId,
      offerTypeIds
    })

    const searchObject = {
      batteryCapacity,
      bodyTypeIds,
      category2Id: categories.category2Ids?.[0],
      categoryId: categories.category1Ids?.[0],
      chargingTimeFastMode,
      chargingTimeStandardMode,
      city,
      colorIds,
      commitmentMonths,
      contractId,
      drivenWheelsIds,
      electricAutonomy,
      environmentalLabels,
      equipments: equipments?.map(equipment => this._cleanInputValue({value: equipment})),
      excludedVehicles: excludedVehicles?.map(({make, makeId, model, modelId}) => ({
        make,
        makeId,
        model: {id: modelId, name: model ? this._cleanInputValue({value: model}) : undefined}
      })),
      fee,
      filteredOfferTypeId,
      financedPrice: isFinanced,
      fuelTypeIds,
      hasPhoto,
      hasReservation,
      hasStock,
      hasWarranty,
      hasInstalment: instalment === undefined ? undefined : this._getHasInstalment(instalment),
      isCertified,
      instalment,
      kms: km,
      ...(hasLocation && {
        location: {
          lat: location.latitude,
          long: location.longitude,
          text: location.text,
          radio: location.distance
        }
      }),
      luggageCapacity,
      entry,
      maxTerms,
      offerTypeId,
      onlyPeninsula,
      page,
      price: isFinanced ? financedPrice : price,
      priceDrop,
      hasPriceDrop: Boolean(priceDrop),
      provinceIds,
      rating,
      sellerTypeId,
      sortBy: term,
      sortOrder: this._orderMap[orderKey],
      subscriptionVehicleState,
      transmissionTypeId,
      year,
      ...(hasCubicCapacity && {
        litres: {from: cubicCapacity.from, to: cubicCapacity.to}
      }),
      ...(hasDoors && {doors}),
      ...(hasHp && {hp: {from: hp.from, to: hp.to}}),
      ...(searchText && {
        searchText: this._cleanInputValue({value: searchText})
      }),
      ...(hasSeats && {seats}),
      ...this.getVehicles(vehicles)
    }

    const searchRequestValueObject = this._searchRequestValueObjectFactory(searchObject)

    return this._searchDataAdsValueObjectFactory({
      breadcrumbs,
      canonical,
      listingSectionId,
      metaData,
      search: searchRequestValueObject
    })
  }
}
